import { Tooltip } from '@mui/material';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { unwrapResult } from '@reduxjs/toolkit';

import { errorNormalizer } from '../../../../shared/Helpers/functions';
import { CustomButton } from '../../../../shared/uiComponents';
import { AdminTypes } from '../../../../components/Menu';
import { useResetCalendarData } from '../../Helpers/hooks';

import { DispatchProperties, useSelector } from '../../../../redux/store';
import { startEventSession } from '../../../../redux/State/clientSlice/sessionSlice';
import { SoapNotesStatuses } from '../../../../redux/API/ClientAPIHelpers/dataCollectionProperties';
import { getEvents } from '../../../../redux/State/clientSlice/calendarSlice';
import { setInfoEvent, setInfoEventData } from '../../../../redux/State/clientSlice/calendarHelperSlice';

const meetAndGreetSessionTypeIds = [7, 8];
const pendingBTSessionDescription = 'You cannot edit session data in Pending BT status';

const SessionActions = () => {
	const dispatch = useDispatch<DispatchProperties>();
	const navigate = useNavigate();
	const { resetData } = useResetCalendarData();

	const [loading, setLoading] = useState<boolean>(false);

	const role = useSelector((state) => state.account.role);
	const days = useSelector((state) => state.calendarHelper.displayedCalendarPageDays);
	const data = useSelector((state) => state.calendarHelper.infoEventData);

	const isAdmin = role?.section.id === AdminTypes.admin;
	const isBT = role?.section.id === AdminTypes.bt;
	const expired =
		SoapNotesStatuses.expired48 === data?.reportStatus?.status ||
		SoapNotesStatuses.expiredWeek === data?.reportStatus?.status;

	const canSkipSession = (data && meetAndGreetSessionTypeIds.includes(data.sessionType.id)) || !isBT;

	const collectDataHandler = () => {
		if (!data) return;
		const { sessionId, client } = data;
		resetData();
		navigate(`/calendar/session/${sessionId}/${client.id}`);
	};

	const startSessionHandler = () => {
		if (!data?.id) return;
		setLoading(true);
		dispatch(startEventSession(data.id))
			.then(unwrapResult)
			.then((response) => {
				if (!response.id || !response.client.id || !days.length) return;
				dispatch(setInfoEventData(response));
				const clientId = data.client.id;
				const userId = data.user.id;

				const eventData = isAdmin ? { days, clientId, userId } : { days };
				dispatch(getEvents(eventData));

				if (!canSkipSession) {
					dispatch(setInfoEvent(false));
					navigate(`/calendar/session/${response.id}/${response.client.id}`);
				}
			})
			.catch(errorNormalizer)
			.finally(() => setLoading(false));
	};

	return (
		<>
			{data && !data.isCompleted ? (
				data.sessionId ? (
					<CustomButton
						title="Collect Data"
						onClick={collectDataHandler}
						className="flex100"
						disabled={expired && !isAdmin}
					/>
				) : (
					<CustomButton
						title={canSkipSession ? 'Collect Data' : 'Start Session'}
						onClick={startSessionHandler}
						loading={loading}
						className="flex100"
						disabled={expired && !isAdmin}
					/>
				)
			) : (
				<Tooltip
					disableHoverListener={SoapNotesStatuses.pendingBT !== data?.reportStatus?.status || isAdmin}
					title={pendingBTSessionDescription}
				>
					<span className="flex100">
						<CustomButton title="Session Ended" disabled={!isAdmin} onClick={collectDataHandler} />
					</span>
				</Tooltip>
			)}
		</>
	);
};

export default SessionActions;
